import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface User {
  id: string;
  email: string;
}

interface GameStats {
  totalGames: number;
  wins: number;
  losses: number;
  totalRewards: number;
  currentStreak: number;
  bestStreak: number;
  pepcoinsEarned: number;
  pepcoinsSpent: number;
}

interface RewardSystemProps {
  user: User | null;
  gameStats: GameStats;
}

interface DailyReward {
  day: number;
  reward: number;
  claimed: boolean;
  type: 'coins' | 'multiplier' | 'bonus';
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  reward: number;
  unlocked: boolean;
  progress: number;
  target: number;
}

export default function RewardSystem({ user, gameStats }: RewardSystemProps) {
  const [dailyRewards, setDailyRewards] = useState<DailyReward[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [currentStreak, setCurrentStreak] = useState(3);
  const [nextRewardIn, setNextRewardIn] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    initializeRewards();
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const initializeRewards = () => {
    // Initialize daily rewards with attractive percentages
    const rewards: DailyReward[] = [
      { day: 1, reward: 10, claimed: true, type: 'coins' },
      { day: 2, reward: 15, claimed: true, type: 'coins' },
      { day: 3, reward: 20, claimed: true, type: 'coins' },
      { day: 4, reward: 25, claimed: false, type: 'coins' },
      { day: 5, reward: 30, claimed: false, type: 'multiplier' },
      { day: 6, reward: 40, claimed: false, type: 'coins' },
      { day: 7, reward: 100, claimed: false, type: 'bonus' }
    ];
    setDailyRewards(rewards);

    // Initialize achievements with attractive reward percentages
    const achievementsList: Achievement[] = [
      {
        id: 'first_game',
        title: 'First Steps',
        description: 'Play your first game - 5% bonus on next 3 games',
        icon: '🎮',
        reward: 50,
        unlocked: gameStats.totalGames >= 1,
        progress: Math.min(gameStats.totalGames, 1),
        target: 1
      },
      {
        id: 'ten_games',
        title: 'Getting Started',
        description: 'Play 10 games - 10% reward boost for 24 hours',
        icon: '🏅',
        reward: 100,
        unlocked: gameStats.totalGames >= 10,
        progress: Math.min(gameStats.totalGames, 10),
        target: 10
      },
      {
        id: 'first_win',
        title: 'Victory!',
        description: 'Win your first game - 15% bonus on all wins today',
        icon: '🏆',
        reward: 75,
        unlocked: gameStats.wins >= 1,
        progress: Math.min(gameStats.wins, 1),
        target: 1
      },
      {
        id: 'win_streak',
        title: 'Hot Streak',
        description: 'Win 5 games in a row - 25% streak multiplier',
        icon: '🔥',
        reward: 200,
        unlocked: gameStats.bestStreak >= 5,
        progress: Math.min(gameStats.bestStreak, 5),
        target: 5
      },
      {
        id: 'coin_collector',
        title: 'Coin Collector',
        description: 'Earn 1000 PepCoins - 20% earning boost for 7 days',
        icon: '💰',
        reward: 150,
        unlocked: gameStats.pepcoinsEarned >= 1000,
        progress: Math.min(gameStats.pepcoinsEarned, 1000),
        target: 1000
      },
      {
        id: 'high_roller',
        title: 'High Roller',
        description: 'Play 100 games - 30% VIP reward multiplier',
        icon: '🎲',
        reward: 500,
        unlocked: gameStats.totalGames >= 100,
        progress: Math.min(gameStats.totalGames, 100),
        target: 100
      }
    ];
    setAchievements(achievementsList);
  };

  const updateCountdown = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const diff = tomorrow.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    setNextRewardIn(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
  };

  const claimDailyReward = async (day: number) => {
    setLoading(true);
    try {
      // Simulate claiming reward
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setDailyRewards(prev => 
        prev.map(reward => 
          reward.day === day ? { ...reward, claimed: true } : reward
        )
      );
      
      // Show success message or update balance
      console.log(`Claimed day ${day} reward!`);
    } catch (error) {
      console.error('Claim reward error:', error);
    } finally {
      setLoading(false);
    }
  };

  const claimAchievement = async (achievementId: string) => {
    setLoading(true);
    try {
      // Simulate claiming achievement
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setAchievements(prev => 
        prev.map(achievement => 
          achievement.id === achievementId ? { ...achievement, unlocked: false } : achievement
        )
      );
      
      console.log(`Claimed achievement: ${achievementId}`);
    } catch (error) {
      console.error('Claim achievement error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRewardIcon = (type: string) => {
    switch (type) {
      case 'coins': return '🪙';
      case 'multiplier': return '⚡';
      case 'bonus': return '🎁';
      default: return '🪙';
    }
  };

  const getRewardColor = (type: string) => {
    switch (type) {
      case 'coins': return 'from-yellow-500/20 to-orange-500/20 border-yellow-500/30';
      case 'multiplier': return 'from-purple-500/20 to-pink-500/20 border-purple-500/30';
      case 'bonus': return 'from-green-500/20 to-emerald-500/20 border-green-500/30';
      default: return 'from-gray-500/20 to-gray-600/20 border-gray-500/30';
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Daily Rewards */}
      <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold flex items-center gap-2">
            <i className="ri-calendar-check-line text-purple-400"></i>
            Daily On-Chain Rewards
          </h3>
          <div className="text-right">
            <div className="text-sm text-gray-400">Next claim in</div>
            <div className="text-lg font-bold text-purple-400">{nextRewardIn}</div>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2 md:gap-4">
          {dailyRewards.map((reward) => (
            <div
              key={reward.day}
              className={`bg-gradient-to-br ${getRewardColor(reward.type)} backdrop-blur-sm border rounded-xl p-3 md:p-4 text-center transition-all duration-300 ${
                reward.day === currentStreak + 1 && !reward.claimed ? 'ring-2 ring-purple-500 scale-105' : ''
              }`}
            >
              <div className="text-2xl md:text-3xl mb-2">{getRewardIcon(reward.type)}</div>
              <div className="text-xs md:text-sm font-bold text-gray-300 mb-1">Day {reward.day}</div>
              <div className="text-sm md:text-base font-bold text-white mb-2">
                {reward.type === 'multiplier' ? `${reward.reward}x` : `${reward.reward}`}
              </div>
              
              {reward.claimed ? (
                <div className="text-xs text-green-400 font-semibold">✓ Minted</div>
              ) : reward.day === currentStreak + 1 ? (
                <button
                  onClick={() => claimDailyReward(reward.day)}
                  disabled={loading}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white text-xs font-bold py-1 px-2 rounded transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  Mint
                </button>
              ) : (
                <div className="text-xs text-gray-500">Locked</div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 text-center text-sm text-gray-400">
          Current streak: {currentStreak} days • Keep playing daily to maintain your on-chain streak!
        </div>
      </div>

      {/* Achievements */}
      <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6">
        <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <i className="ri-trophy-line text-yellow-400"></i>
          NFT Achievement Badges
        </h3>

        <div className="grid md:grid-cols-2 gap-4">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className={`bg-gradient-to-r backdrop-blur-sm border rounded-xl p-6 transition-all duration-300 ${
                achievement.unlocked
                  ? 'from-yellow-900/20 to-orange-900/20 border-yellow-500/30 hover:scale-105'
                  : 'from-gray-900/20 to-gray-800/20 border-gray-600/30'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">{achievement.icon}</div>
                  <div>
                    <h4 className="font-bold text-white">{achievement.title}</h4>
                    <p className="text-sm text-gray-400">{achievement.description}</p>
                  </div>
                </div>
                
                {achievement.unlocked && (
                  <button
                    onClick={() => claimAchievement(achievement.id)}
                    disabled={loading}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    Mint NFT
                  </button>
                )}
              </div>

              {/* Progress Bar */}
              <div className="mb-2">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>Progress</span>
                  <span>{achievement.progress}/{achievement.target}</span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      achievement.unlocked
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                        : 'bg-gradient-to-r from-gray-600 to-gray-500'
                    }`}
                    style={{ width: `${(achievement.progress / achievement.target) * 100}%` }}
                  ></div>
                </div>
              </div>

              <div className="text-right">
                <span className="text-sm font-bold text-yellow-400">+{achievement.reward} PEP</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reward Multipliers */}
      <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-6">
        <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <i className="ri-flashlight-line text-purple-400"></i>
          Active DeFi Multipliers
        </h3>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-black/30 rounded-xl p-4 text-center">
            <div className="text-3xl mb-2">⚡</div>
            <div className="font-bold text-purple-400">+15% Staking</div>
            <div className="text-sm text-gray-400">Next 3 games</div>
            <div className="text-xs text-green-400 mt-1">Active</div>
          </div>
          
          <div className="bg-black/30 rounded-xl p-4 text-center">
            <div className="text-3xl mb-2">🔥</div>
            <div className="font-bold text-orange-400">+8% APY</div>
            <div className="text-sm text-gray-400">Streak bonus</div>
            <div className="text-xs text-yellow-400 mt-1">Building</div>
          </div>
          
          <div className="bg-black/30 rounded-xl p-4 text-center">
            <div className="text-3xl mb-2">🎯</div>
            <div className="font-bold text-green-400">+12% Yield</div>
            <div className="text-sm text-gray-400">Hour bonus</div>
            <div className="text-xs text-cyan-400 mt-1">47 min left</div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border border-yellow-500/30 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-yellow-400 font-bold">🌟 Daily DeFi Bonus Active</div>
              <div className="text-sm text-gray-400">+6% on all token rewards today</div>
            </div>
            <div className="text-right">
              <div className="text-yellow-400 font-bold text-lg">+6%</div>
              <div className="text-xs text-gray-400">Expires: 11:59 PM</div>
            </div>
          </div>
        </div>
      </div>

      {/* Reward Summary */}
      <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6">
        <h3 className="text-xl font-bold mb-4">On-Chain Reward Summary</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-400">{gameStats.pepcoinsEarned}</div>
            <div className="text-sm text-gray-400">Tokens Earned</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">
              {dailyRewards.filter(r => r.claimed).length}
            </div>
            <div className="text-sm text-gray-400">NFTs Minted</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-400">
              {achievements.filter(a => a.unlocked).length}
            </div>
            <div className="text-sm text-gray-400">Badges Earned</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{currentStreak}</div>
            <div className="text-sm text-gray-400">Day Streak</div>
          </div>
        </div>
      </div>
    </div>
  );
}
